import { ReactElement, useEffect, useRef, useState } from 'react';
import {
  NoDataCell,
  TableHead,
  TableRowCell,
} from '@/components/Table/components';
import { TableColumn, TableVariant } from '@/components/Table/types';
import { ProcessWorkflowTask, WorkflowWithTasks } from '../../types';
import { t } from 'i18next';
import { TaskRow } from '../TaskRow';
import { twMerge } from 'tailwind-merge';

interface WorkflowRowProps {
  workflow: WorkflowWithTasks;
  workflowsColumns: TableColumn<WorkflowWithTasks>[];
  taskColumns: TableColumn<ProcessWorkflowTask>[];
  variant: TableVariant;
  noOutline?: boolean;
  selected?: boolean;
  showDots?: boolean;
  depth?: number;
  isTree?: boolean;
  noChildDots?: boolean;
  onRowHoverStyling?: string;
  collapseClassname?: string;
}

export const WorkflowRow = ({
  workflow,
  workflowsColumns,
  taskColumns,
  depth = 0,
  isTree,
  variant,
  noOutline,
  selected,
  showDots,
  noChildDots,
  onRowHoverStyling,
  collapseClassname,
}: WorkflowRowProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const tdRef = useRef<HTMLTableCellElement>(null);
  const [tdHeight, setTdHeight] = useState(0);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (tdRef.current) {
        setTdHeight(tdRef.current.offsetHeight);
      }
    });
  }, [workflow, isOpen]);

  return (
    <>
      <TableRowCell
        row={workflow}
        cols={workflowsColumns}
        depth={depth}
        isTree={isTree}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        noOutline={noOutline}
        onRowClick={() => setIsOpen(!isOpen)}
        selected={selected}
        variant={variant}
        showDots={showDots}
        showBlueDot={false}
        noChildDots={noChildDots}
        onRowHoverStyling={onRowHoverStyling}
        onlyCollapse
        collapseClassname={collapseClassname}
      />
      {isOpen && (
        <tr>
          <td colSpan={11} className="pl-6" ref={tdRef}>
            <div className="relative">
              <div
                className={twMerge(
                  'absolute left-[17px] top-[-4px] border-2 text-brightGray',
                )}
                style={{ height: `${tdHeight + 4}px` }}
              ></div>
            </div>
            <div className="my-2 ml-10 rounded-md bg-brightGray p-3">
              <table className="w-full">
                <thead className="bg-inherit">
                  <TableHead columns={taskColumns} variant="medium" isTree />
                </thead>
                <tbody>
                  {workflow?.rows.length === 0 ? (
                    <NoDataCell
                      colSpan={taskColumns.length}
                      variant={'medium'}
                      noResultsLabel={t('no_tasks_available')}
                    />
                  ) : (
                    workflow?.rows?.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        taskColumns={taskColumns}
                        variant={variant}
                        onRowHoverStyling={onRowHoverStyling}
                        noOutline={noOutline}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
